import { ITaggedArticlesModuleFields } from 'contentful/types/contentful-modules'
import { getTaggedArticles } from 'src/utils/blog/blog-utils'
import {
  BlogTaggedArticle,
  TaggedArticlesModuleProps,
} from '@/components/tagged-articles/tagged-articles.module'
import {
  MappingContext,
  ModuleMapper,
} from '@/components/content/contentMapper'

export enum TaggedArticlesLayoutOption {
  CAROUSEL = 'Carousel',
  SHOW_ALL = 'Show all',
}

const mapTaggedArticles = (
  fields: ITaggedArticlesModuleFields,
  context: MappingContext,
  blogTaggedPages: BlogTaggedArticle[],
): TaggedArticlesModuleProps => {
  const tagSlug = fields.tag?.fields?.slug
  return {
    blogTaggedPages,
    carouselTitle: fields.carouselTitle,
    slug: context.slug !== tagSlug ? '/' + tagSlug : null,
    layoutOption: fields.layoutOption as TaggedArticlesLayoutOption,
  }
}

const fetchTagRelatedArticlesOrEmpty = async (
  fields: ITaggedArticlesModuleFields,
  mappingContext: MappingContext,
): Promise<BlogTaggedArticle[] | undefined> => {
  const type =
    fields.layoutOption === TaggedArticlesLayoutOption.CAROUSEL
      ? 'Blog carousel'
      : 'Blog Tagged Articles module'
  try {
    const tagRelatedArticles = await getTaggedArticles({
      mappingContext,
      tagId: fields?.tag?.sys?.id,
      limit:
        fields.layoutOption === TaggedArticlesLayoutOption.CAROUSEL ? 9 : 100,
    })

    if (!tagRelatedArticles.length) {
      mappingContext.mappingErrors.push(
        `Property ${type} "${fields.id}" had zero results and is therefore removed`,
      )
      return
    }
    return tagRelatedArticles?.map((articles) => ({
      ...articles,
      link: `/${articles?.link}`,
    }))
  } catch (e) {
    const message = e.errors?.map((e) => e.msg)?.join(', ') || e.message
    mappingContext.mappingErrors.push(
      `${type} "${fields.id}" had the following error: "${message}"`,
    )
  }
}

export const taggedArticlesModuleMapper: ModuleMapper<
  ITaggedArticlesModuleFields,
  BlogTaggedArticle[]
> = {
  prepare: async (
    fields: ITaggedArticlesModuleFields,
    mappingContext: MappingContext,
  ) => {
    const tagRelatedArticles = await fetchTagRelatedArticlesOrEmpty(
      fields,
      mappingContext,
    )
    return { data: tagRelatedArticles || [], skipModule: !tagRelatedArticles }
  },
  map: (fields, context, data) => {
    return {
      content: mapTaggedArticles(fields, context, data),
      handlesLayout: true,
    }
  },
}
